import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { AuthClient } from '../../static/public/gid-sdk.es';
import { AuthLiteClient } from '../../static/lite/gid-sdk.es';
import { reducerLite, reducerPublic } from './reducer';
import { initialAuthStateLite, initialAuthStatePublic } from './auth-state';
import { buttonsConfig, config } from './config';
import type { AuthProviderOptions, ActionPublic } from './types';
import { buildEventsConfig, createQueryParams, getHeaders, logError, normalizedError, parseAuthResponse, setOtpTimeout } from '@/utils';
import { getClientConfig, formatClientConfig } from '@/hooks/helpers';
import { handleFetchToken } from './api';

/**
 * ```jsx
 * <AuthProvider>
 *   <MyApp />
 * </AuthProvider>
 * ```
 *
 * Provides the AuthContext to its child components.
 */
export function AuthProvider({ children, context }: AuthProviderOptions) {
  const location = useLocation();
  const [publicState, publicDispatch] = React.useReducer(reducerPublic, initialAuthStatePublic);
  const [score, setScore] = React.useState<string>('');
  const [userInfo, setUserInfo] = React.useState<string>('');
  const [changedConfigData, setChangedConfigData] = React.useState<{ scope?: string, redirectUri?: string, flowName?: string }>({ scope: '', redirectUri: '', flowName: '' });
  const didInitialize = React.useRef(false);
  const [clientLite] = React.useState(() => {
    const authClientConfig = formatClientConfig();
    return new AuthLiteClient({
      ...authClientConfig,
      redirectUrl: authClientConfig.redirectUri,
      debug: authClientConfig.debugConsole,
      eventsConfig: buildEventsConfig(authClientConfig.authUrl),
    });
  });
  const [ksid, setKsid] = React.useState<string>('');
  const [liteState, liteDispatch] = React.useReducer(reducerLite, initialAuthStateLite);
  const [clientPublic] = React.useState(() => {
    // TODO filter unnecessary fields
    const authClientConfig = formatClientConfig();
    return new AuthClient({
      ...authClientConfig,
      debugConsole: authClientConfig.debugConsole,
      debugTrack: authClientConfig.debugTrack,
      sentryEnabled: authClientConfig.sentryEnabled,
      preventAutoStorageCleanup: authClientConfig.preventAutoStorageCleanup,
      requestTimeout: authClientConfig.requestTimeout ? parseInt(authClientConfig.requestTimeout) : null,
      headers: getHeaders(authClientConfig.headers),
      eventsConfig: buildEventsConfig(authClientConfig.authUrl),
      keycloak: {
        keycloakEnabled:authClientConfig.keycloakEnabled,
        authUrl: authClientConfig.keycloak_authUrl,
        clientId: authClientConfig.keycloak_clientId,
        realmName: authClientConfig.keycloak_realmName,
        idpName: authClientConfig.keycloak_idpName,
      },
      buttons: buttonsConfig,
      callback: (response) => {
        const {
          error,
          error_description,
          access_token = '',
          id_token = '',
          refresh_token = '',
        } = response;
        console.log('Token Response: ', response)
        const action: ActionPublic = access_token && id_token
          ? { type: 'LOGIN_COMPLETE', access_token, id_token, refresh_token }
          : { type: 'ERROR', error: error_description || error || 'Ошибка при получении токена' };
        publicDispatch(action);
      },
    })
  });

  React.useEffect(() => {
    const notify = !sessionStorage.getItem('sdk_init');
    const CODE_RE = /[?&]code=[^&]+/;
    const hasCode = CODE_RE.test(window.location.search);
    if (hasCode) {
      publicDispatch({ type: 'LOGIN_STARTED' });
    }
    if (location.pathname !== '/qr') {
      if (notify) {
        sessionStorage.setItem('sdk_init', 'true');
      }
      clientPublic.init().then(() => {
        didInitialize.current = true;
        publicDispatch({ type: 'INITIALISED', notify });
      });
    };
  }, [location]);

  const changeConfigData = React.useCallback(({ scopes, redirectUri, flowName }: { scopes?: string, redirectUri?: string, flowName?: string }) => {
    setChangedConfigData({ scope: scopes, redirectUri, flowName })
  }, []);

  React.useEffect(() => {
    const authClientConfig = getClientConfig();
    const goya_proxy_url = authClientConfig.goya_proxy_url;

    if (goya_proxy_url && (authClientConfig.trackers || 'goya,tracking_receiver').split(',').map((t: string) => t.trim()).includes('goya')) {
      const handleMessage = async (e: MessageEvent): Promise<void> => {
        try {
          const goyaData = JSON.parse(e.data as unknown as string);
          if (goyaData.event_group === 'iframe') {
            const goyaParams = {
              ...goyaData,
              cid: authClientConfig.cid || undefined,
              session_id: authClientConfig.session_id || undefined,
              appmetrica_device_id: authClientConfig.appmetrica_device_id || undefined,
              product_name: authClientConfig.product_name || undefined,
            }
            const url = `${goya_proxy_url}?${createQueryParams(goyaParams)}`;
            fetch(url)
          }
        } catch { }
      }
      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      }
    }
  }, []);
  
  React.useEffect(() => {
    if (didInitialize.current) {
      return;
    }
    clientLite.init().then(() => {
      didInitialize.current = true;
      liteDispatch({ type: 'INITIALISED' });
    }).catch((e: unknown) => {
      logError(e)

    });
  }, [clientLite]);

  const signIn = React.useCallback(
    async (phone: string): Promise<void> => {
      liteDispatch({ type: 'SIGN_IN_STARTED' });
      try {
        const { wait_seconds } = await clientLite.signIn(phone);
        setOtpTimeout(wait_seconds);
        liteDispatch({ type: 'SIGN_IN_COMPLETE' });
      } catch (error) {
        liteDispatch({ type: 'ERROR', error: normalizedError(error) });
        logError(error)
      }
    },
    [clientLite],
  );

  const loginWithOtp = React.useCallback(
    async (code: string): Promise<void> => {
      let token = '';
      let refresh_token = '';
      liteDispatch({ type: 'LOGIN_OTP_STARTED' });
      try {
        const data = await clientLite.loginWithOtp(code);
        if (data?.redirect_to) {
          const { code, state, ksid: kfpKsid } = parseAuthResponse(data.redirect_to);
          setKsid(kfpKsid || '');
          const result = await handleFetchToken({ code, state, ksid: kfpKsid });
          token = result?.access_token ?? '';
          refresh_token = result?.refresh_token ?? '';
        }
        if (!token) {
          liteDispatch({ type: 'ERROR', error: normalizedError('Ошибка при получении токена') });
        } else if (data && 'error_description' in data) {
          const error = data.error_description;
          liteDispatch({ type: 'ERROR', error: normalizedError(error) });
          logError(data)
        } else {
          liteDispatch({ type: 'LOGIN_OTP_COMPLETE', token, refresh_token });
        }
      } catch (error) {
        liteDispatch({ type: 'ERROR', error: normalizedError(error) });
        logError(error)
      }
    },
    [clientLite],
  );

  const logoutLite = React.useCallback(
    async (): Promise<void> => {
      await clientLite.revoke(liteState.token);
      liteDispatch({ type: 'LOGOUT' });
    },
    [clientLite, liteState.token],
  );

  const loginWithRedirect = React.useCallback(
    async (): Promise<void> => {
      publicDispatch({ type: 'LOGIN_STARTED' });
      await clientPublic.loginWithRedirect({...changedConfigData, flowName: 'redirect' });
    },
    [clientPublic, changedConfigData],
  );

  const runForcedRedirect = React.useCallback(
    async (
      loginTag: 'silent_login' | 'force_login',
      type: 'redirect' | 'popup' = 'redirect',
    ): Promise<void> => {
      publicDispatch({ type: 'LOGIN_STARTED' });
      try {
        await clientPublic.loginWithForcedRedirect(loginTag, type);
      } catch (error) {
        publicDispatch({ type: 'ERROR', error: error instanceof Error ? error.message : String(error) });
      }
    },
    [clientPublic],
  );

  const loginWithQuuidRedirect = React.useCallback(
    async (): Promise<void> => {
      publicDispatch({ type: 'LOGIN_STARTED' });
      await clientPublic.loginWithQuuidRedirect({...changedConfigData, flowName: 'quuid_redirect' });
    },
    [clientPublic, changedConfigData],
  );

  const loginWithPopup = React.useCallback(
    async (): Promise<void> => {
      // publicDispatch({ type: 'LOGIN_STARTED' });
      await clientPublic.loginWithPopup({...changedConfigData, flowName: 'popup' });
    },
    [clientPublic, changedConfigData],
  );

  const loginWithNewTab = React.useCallback(
    async (): Promise<void> => {
      // publicDispatch({ type: 'LOGIN_STARTED' });
      await clientPublic.loginWithNewTab({...changedConfigData, flowName: 'new_tab' });
    },
    [clientPublic, changedConfigData],
  );

  const logout = React.useCallback(
    async (id_token: string): Promise<void> => {
      await clientPublic.sessionLogout(id_token);
      publicDispatch({ type: 'LOGOUT' });
    },
    [clientPublic],
  );

  const revoke = React.useCallback(
    async (access_token: string): Promise<void> => {
      try {
        await clientPublic.logout(access_token);
        publicDispatch({ type: 'LOGOUT' });
      } catch (error) {
        logError(error)
        publicDispatch({ type: 'LOGOUT' });
      }
    },
    [clientPublic],
  );

  const refreshToken = React.useCallback(
    async (refresh_token: string): Promise<void> => {
      if (liteState.token) {
        try {
          const result = await clientLite.refreshToken(refresh_token);
          const token = result?.access_token ?? '';
          const new_refresh_token = result?.refresh_token ?? refresh_token;
          if (!token) {
            liteDispatch({ type: 'ERROR', error: normalizedError('Ошибка при обновлении токена') });
          } else {
            liteDispatch({ type: 'LOGIN_OTP_COMPLETE', token, refresh_token: new_refresh_token });
          }
        } catch (error) {
          liteDispatch({ type: 'ERROR', error: normalizedError(error) });
          logError(error);
        }
        return;
      }

      await clientPublic.refreshToken(refresh_token);
    },
    [clientPublic, clientLite, liteState.token],
  );

  const getForcedAuthVariants = React.useCallback(
    (): string[] => {
      return clientPublic.getForcedAuthVariants();
    },
    [clientPublic],
  );

  const getWidget = React.useCallback(
    async (): Promise<void> => {
      const authClientConfig = getClientConfig();
      await clientPublic.getWidget(authClientConfig.clientId, 'redirect', changedConfigData);
    },
    [clientPublic, changedConfigData],
  );

  const getTokenManually = React.useCallback(
    async (): Promise<void> => {
      const authClientConfig = config;
      console.log('authClientConfig', authClientConfig);
      const result = await clientPublic.getTokenManually(authClientConfig.backendUrl);
      if (result) {
        const {
          access_token = '',
          id_token = '',
          refresh_token = '',
        } = result;
        const action: ActionPublic = access_token && id_token
          ? { type: 'LOGIN_COMPLETE', access_token, id_token, refresh_token }
          : { type: 'ERROR', error: 'Ошибка при получении токена' };
        publicDispatch(action);
      }
    },
    [clientPublic],
  );

  const sendAuthSuccessEvent = React.useCallback(
    async (): Promise<void> => {
      await clientPublic.sendAuthSuccessEvent();
    },
    [clientPublic],
  );

  const sendTokenReceivedEvent = React.useCallback(
    async (): Promise<void> => {
      await clientPublic.sendTokenReceivedEvent();
    },
    [clientPublic],
  );

  const openPersonalAccountCards = React.useCallback(
    async (
      callback?: (params: { type: 'closed' }) => void,
    ): Promise<void> => {
      await clientPublic.openPersonalAccountCards(callback);
    },
    [clientPublic],
  );

  const getUserInfo = React.useCallback(
    async (accessToken: string) => {
      return clientPublic.getUserInfo(accessToken);
    },
    [clientPublic],
  );

  const refreshSdkConfigFromServer = React.useCallback(
    async (): Promise<void> => {
      await Promise.all([
        clientPublic.refreshSdkConfigFromServer(),
        clientLite.refreshSdkConfigFromServer(),
      ]);
    },
    [clientPublic, clientLite],
  );

  const version: string = React.useMemo(
    () => clientPublic.version.replace(/'/g, ''),
    [],
  );

  const build_number: string = React.useMemo(
    () => clientPublic.build_number.replace(/'/g, ''),
    [],
  );

  const contextValue = React.useMemo(() => {
    return {
      ...liteState,
      ...publicState,
      refresh_token: liteState.token ? liteState.refresh_token : publicState.refresh_token,
      isLoading: liteState.isLoading || publicState.isLoading,
      loginWithRedirect,
      runForcedRedirect,
      loginWithQuuidRedirect,
      loginWithPopup,
      loginWithNewTab,
      logout,
      revoke,
      refreshToken,
      getForcedAuthVariants,
      getWidget,
      getTokenManually,
      sendAuthSuccessEvent,
      sendTokenReceivedEvent,
      openPersonalAccountCards,
      getUserInfo,
      changeConfigData,
      refreshSdkConfigFromServer,
      setScore,
      setUserInfo,
      logoutLite,
      signIn,
      loginWithOtp,
      score,
      userInfo,
      version,
      build_number,
      ksid,
    };
  }, [
    liteState,
    publicState,
    loginWithRedirect,
    runForcedRedirect,
    loginWithQuuidRedirect,
    loginWithPopup,
    loginWithNewTab,
    logout,
    revoke,
    refreshToken,
      getForcedAuthVariants,
    getWidget,
    getTokenManually,
    sendAuthSuccessEvent,
    sendTokenReceivedEvent,
    openPersonalAccountCards,
    getUserInfo,
    changeConfigData,
    refreshSdkConfigFromServer,
    setScore,
    setUserInfo,
    logoutLite,
    signIn,
    loginWithOtp,
    score,
    userInfo,
    version,
    build_number,
    ksid,
  ]);

  return (
    <context.Provider value={contextValue}>
      {children}
    </context.Provider>
  );
}
